// File: frontend/src/pages/reports/StudentReport.jsx

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSchool } from '../../context/SchoolContext';
import { exportToPDF } from '../../utils/pdfExport';
import api from '../../utils/axios';

const StudentReport = () => {
  const { school } = useSchool();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchReport = async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await api.get('/api/v1/reports/students');
        if (res.data?.success) {
          setData(res.data.data);
        } else {
          setError(res.data?.message || 'Failed to load student report.');
        }
      } catch (err) {
        console.error('Error loading student report:', err);
        setError(err.response?.data?.message || err.message || 'Error loading student report.');
      } finally {
        setLoading(false);
      }
    };
    fetchReport();
  }, []);

  /**
   * Export Student Report to PDF
   */
  const handleExportPDF = () => {
    if (!data || !data.by_class) return;

    const headers = ['Class', 'Total Students', 'Active', 'Inactive'];
    const rows = data.by_class.map((c) => [
      `Class ${c.class_id}`,
      c.total || 0,
      c.active || 0,
      c.inactive || 0
    ]);

    exportToPDF('Student Demographics & Distribution Report', headers, rows, school?.name);
  };

  const summary = data?.summary || { total_students: 0, active_students: 0, inactive_students: 0 };
  const genderList = data?.by_gender || [];
  const maleCount = genderList.find((g) => g.gender?.toLowerCase() === 'male')?.count || 0;
  const femaleCount = genderList.find((g) => g.gender?.toLowerCase() === 'female')?.count || 0;

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Top Navbar */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/reports" className="w-10 h-10 rounded-xl bg-slate-100 text-slate-700 hover:bg-slate-200 flex items-center justify-center font-bold transition">
              ←
            </Link>
            <div>
              <h1 className="font-bold text-slate-800 text-lg leading-tight">Student Enrollment Report</h1>
              <p className="text-xs text-slate-500">Demographic distribution & status analytics</p>
            </div>
          </div>

          <button
            onClick={handleExportPDF}
            disabled={loading || !data}
            className="bg-primary hover:bg-blue-600 text-white font-bold px-5 py-2.5 rounded-xl text-xs shadow-md transition disabled:opacity-50 flex items-center gap-2"
          >
            📄 Export PDF Report
          </button>
        </div>
      </header>

      {/* Main Body */}
      <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        
        {error && (
          <div className="p-4 bg-red-50 text-red-700 border border-red-200 rounded-2xl text-sm font-semibold">
            {error}
          </div>
        )}

        {/* 5 Stats Cards Grid */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm">
            <span className="text-slate-400 text-xs font-bold uppercase block mb-1">Total Students</span>
            <span className="text-2xl font-black text-slate-900">{loading ? '...' : summary.total_students}</span>
          </div>
          <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm">
            <span className="text-slate-400 text-xs font-bold uppercase block mb-1">Active</span>
            <span className="text-2xl font-black text-emerald-600">{loading ? '...' : summary.active_students}</span>
          </div>
          <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm">
            <span className="text-slate-400 text-xs font-bold uppercase block mb-1">Inactive</span>
            <span className="text-2xl font-black text-red-600">{loading ? '...' : summary.inactive_students}</span>
          </div>
          <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm">
            <span className="text-slate-400 text-xs font-bold uppercase block mb-1">Male Students</span>
            <span className="text-2xl font-black text-blue-600">{loading ? '...' : maleCount}</span>
          </div>
          <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm">
            <span className="text-slate-400 text-xs font-bold uppercase block mb-1">Female Students</span>
            <span className="text-2xl font-black text-purple-600">{loading ? '...' : femaleCount}</span>
          </div>
        </div>

        {/* Class-wise Distribution Table */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="p-6 border-b border-slate-100 flex items-center justify-between">
            <h2 className="font-bold text-slate-900 text-base">Class-wise Enrollment Summary</h2>
            <span className="text-xs text-slate-400 font-semibold">{data?.by_class?.length || 0} Classes Registered</span>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs font-sans">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-slate-500 font-semibold uppercase">
                  <th className="py-3.5 px-6">Class</th>
                  <th className="py-3.5 px-6 text-center">Total Students</th>
                  <th className="py-3.5 px-6 text-center">Active</th>
                  <th className="py-3.5 px-6 text-center">Inactive</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-slate-800 font-medium">
                {loading ? (
                  <tr>
                    <td colSpan="4" className="py-12 text-center text-slate-400 font-medium">Loading report data...</td>
                  </tr>
                ) : !data?.by_class || data.by_class.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="py-12 text-center text-slate-400 font-medium">No class data found.</td>
                  </tr>
                ) : (
                  data.by_class.map((c) => (
                    <tr key={c.class_id} className="hover:bg-slate-50/80 transition">
                      <td className="py-3.5 px-6 font-bold text-slate-900 text-sm">Class {c.class_id}</td>
                      <td className="py-3.5 px-6 text-center font-bold text-slate-900 text-sm">{c.total}</td>
                      <td className="py-3.5 px-6 text-center font-semibold text-emerald-600">{c.active}</td>
                      <td className="py-3.5 px-6 text-center font-semibold text-red-600">{c.inactive}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

      </main>
    </div>
  );
};

export default StudentReport;
